import { Injectable, inject, signal } from '@angular/core';
import { FloatingPanelService } from '@shared/floating-panel/floating-panel.service';
import { HdGateInfoOpenBehavior } from '../bodygraph/gate-info/hd-gate-info-requested-context.model';
import { loadHdChartGlobalSettings } from '../bodygraph/chart-tabs/hd-chart-global-settings';
import { HdContentLanguageService } from '../bodygraph/hd-content-language.service';
import { HdGateInfoService } from '../bodygraph/hd-gate-info.service';
import { HdThroatVoicesPanelComponent } from './hd-throat-voices-panel.component';
import {
  HD_THROAT_VOICES_PANEL_KEY,
  HdThroatVoicesPanelData,
  hdIsThroatVoiceGate,
} from './hd-voices.metadata';

/**
 * Opent het voices-paneel van de keel (Communicatie cheat sheet).
 *
 * Volgt hetzelfde patroon als `HdFearsPanelService`: de dock-/dialoogkeuze
 * komt uit de globale `gateInfoOpenBehavior`-setting die de chart-pagina's
 * live doorgeven via {@link setOpenBehavior}. Leestekst → altijd de
 * `'full'`-dockzone. Eén paneel (key `throat-voices`); opnieuw openen voor
 * een andere keelpoort hergebruikt het paneel en scrollt via `focusGate` naar
 * de juiste sectie.
 */
@Injectable({ providedIn: 'root' })
export class HdVoicesPanelService {
  private readonly floatingPanels = inject(FloatingPanelService);
  private readonly gateInfo = inject(HdGateInfoService);
  private readonly contentLang = inject(HdContentLanguageService);

  private readonly openBehavior = signal<HdGateInfoOpenBehavior>(
    loadHdChartGlobalSettings().gateInfoOpenBehavior,
  );

  /** Live-sync van de `gateInfoOpenBehavior`-setting door de chart-pagina's. */
  setOpenBehavior(behavior: HdGateInfoOpenBehavior): void {
    this.openBehavior.set(behavior);
  }

  /** Opent het voices-paneel bovenaan (zonder focus-poort), bv. vanaf het keelcentrum. */
  openForThroat(origin?: { x: number; y: number }): void {
    this.open(null, origin);
  }

  /**
   * Opent het voices-paneel gescrolld naar de detailsectie van deze keelpoort.
   * Geen voice-poort → no-op.
   */
  openForGate(gate: number, origin?: { x: number; y: number }): void {
    if (!hdIsThroatVoiceGate(gate)) {
      return;
    }
    this.open(gate, origin);
  }

  private open(focusGate: number | null, origin?: { x: number; y: number }): void {
    const behavior = this.openBehavior();
    const docked = behavior === 'full-docked-keep' || behavior === 'full-docked-autoclose';
    const data: HdThroatVoicesPanelData = { focusGate };

    const ref = this.floatingPanels.openOrFocus<HdThroatVoicesPanelComponent, HdThroatVoicesPanelData>({
      key: HD_THROAT_VOICES_PANEL_KEY,
      component: HdThroatVoicesPanelComponent,
      data,
      dockTab: { label: this.dockTabLabel(), icon: 'lips' },
      dockZone: 'full',
      openInitiallyDocked: docked,
      redockOnReopen: docked,
      startPosition: origin != null
        ? { preferred: origin, hint: 'prefer-right-of-anchor' }
        : 'center',
    });
    // Ook een al open paneel de nieuwe focus-poort aanreiken (verse referentie
    // zodat de auto-scroll opnieuw wapent, ook bij dezelfde poort).
    ref.replaceData({ ...data });
  }

  /** Kort dock-label ("Voices") uit de communication-scope, met fallback. */
  private dockTabLabel(): string {
    const lang = this.contentLang.effectiveLang();
    const label =
      this.gateInfo.cheatSheetTextIn('communication', 'voicesPanel.dockLabel', lang) ||
      this.gateInfo.cheatSheetTextIn('communication', 'voicesPanel.dockLabel', 'nl');
    return label || 'Voices';
  }
}
